import React, {
	useMemo,
	useCallback,
	useEffect,
} from 'react';
import { useQuery } from 'react-query';
import debounce from 'lodash.debounce';
import { FeatureFlags } from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { getVessels } from '@client/lib/api';
import getVesselOptions from '@client/utils/getVesselOptions';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import Select from './Select';

const VesselSelector = ({
	includeMarketVessels = false,
	includeVesselIndex = false,
	showNoVessel = false,
	placeholder = 'Select vessel',
	value,
	variant = 'borderless',
	defaultValue,
	onChange,
	disabled,
	...props
}: {
	includeMarketVessels?: boolean;
	includeVesselIndex?: boolean;
	showNoVessel?: boolean;
	placeholder?: string;
	value?: number | null;
	variant?: 'borderless' | 'filled' | 'outlined';
	onChange?: (v: number | null) => void;
	defaultValue?: number | null;
	disabled?: boolean;
}) => {
	const [searchQuery, setSearchQuery] = React.useState<string>('');
	const hasVesselIndex = useFeatureFlag(FeatureFlags.VESSELINDEX);

	const { data: vessels, isLoading } = useQuery(
		['vessels', searchQuery],
		() => getVessels({
			vesselIndexSearchQuery: includeVesselIndex ? searchQuery : null,
			includeMarketVessels,
			includeVesselIndex: includeVesselIndex && hasVesselIndex,
		}),
		{
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const handleChange = (v: number | null) => {
		if (typeof v === 'string' || typeof v === 'boolean' || Array.isArray(v)) {
			return;
		}

		if (onChange != null && typeof onChange === 'function') {
			onChange(v);
		}
	};

	const debouncedSetSearchQuery = useMemo(() => debounce((query: string) => {
		setSearchQuery(query);
	}, 300), []);

	const handleSearch = useCallback((query: string) => {
		if (includeVesselIndex) {
			debouncedSetSearchQuery(query);
		}
	}, [includeVesselIndex, debouncedSetSearchQuery]);

	const sorted = useMemo(() => {
		if (!vessels) {
			return [];
		}

		return vessels.sort((a, b) => a.name.localeCompare(b.name));
	}, [vessels]);

	const marketVessels = useMemo(() => {
		const fromMarket = getVesselOptions(sorted ?? [], 'marketVessels');
		const fromVesselIndex = getVesselOptions(sorted ?? [], 'vesselIndex');

		const marketIds = new Set(fromMarket.map((v) => v.imo));
		const filteredVesselIndex = fromVesselIndex.filter((v) => !marketIds.has(v.imo));

		return [...fromMarket, ...filteredVesselIndex];
	}, [sorted]);

	const dataSource = useMemo(() => {
		const options = [
			...(showNoVessel ? [{
				label: 'No vessel',
				value: -1,
			}] : []),
			{
				label: 'Owned vessels',
				options: getVesselOptions(sorted ?? [], 'myVessels'),
			},
			...(includeMarketVessels ? [{
				label: 'Market vessels',
				options: marketVessels,
			}] : []),
		];

		return options;
	}, [sorted, showNoVessel, includeMarketVessels, marketVessels]);

	useEffect(() => {
		return () => {
			debouncedSetSearchQuery.cancel();
		};
	}, [debouncedSetSearchQuery]);

	return (
		<Select
			disabled={disabled}
			onSearch={handleSearch}
			value={value}
			loading={isLoading}
			showSearch
			allowClear
			defaultValue={defaultValue}
			variant={variant}
			placeholder={placeholder}
			options={dataSource}
			dropdownStyle={{ width: 350 }} // dropdown width
			optionRender={(option) => (option.value === -1 ? option.label : (
				<div>
					<strong>
						{option.label}
						{' '}
						(
						{option.data.imo}
						)
					</strong>
					<div style={{ fontSize: 12, color: '#666' }}>
						dwt:
						{' '}
						{option.data.dwt == null ? 'N/A' : formatNumber(option.data.dwt, { separateThousands: true })}
					</div>
				</div>
			))}
			{...props}
			onChange={handleChange}
		/>
	);
};

export default VesselSelector;
